import { useState, useRef, useEffect, useCallback } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

// ─── shared constants ──────────────────────────────────────────────────────────

export const GS = String.fromCharCode(29);

export const SCANNER_THRESHOLD_MS = 80;
export const SCANNER_MIN_LENGTH   = 4;
export const SCANNER_FINALIZE_DELAY_MS = 140;

// USB scanner on Windows with Russian keyboard layout sends Cyrillic instead of Latin.
// Map each Cyrillic key back to its positional US-QWERTY equivalent.
export const RU_TO_LATIN_MAP = Object.fromEntries([
  ...'йцукенгшщзхъ'.split('').map((c, i) => [c, 'qwertyuiop[]'[i]]),
  ...'фывапролджэ'.split('').map((c, i)  => [c, "asdfghjkl;'"[i]]),
  ...'ячсмитьбю'.split('').map((c, i)    => [c, 'zxcvbnm,.'[i]]),
  ...'ЙЦУКЕНГШЩЗХЪ'.split('').map((c, i) => [c, 'QWERTYUIOP{}'[i]]),
  ...'ФЫВАПРОЛДЖЭ'.split('').map((c, i)  => [c, 'ASDFGHJKL:"'[i]]),
  ...'ЯЧСМИТЬБЮ'.split('').map((c, i)    => [c, 'ZXCVBNM<>'[i]]),
]);

// GS1 AIs with fixed data length — no GS separator needed after them.
export const GS1_FIXED_AIs = new Set([
  '00','01','02','03','04',
  '11','12','13','14','15','16','17','18','19','20',
]);

// Честный знак — это всегда Data Matrix (GS1).
export const CAMERA_FORMATS = [BarcodeFormat.DATA_MATRIX];

// Дополнительный штрихкод на этикетке (не ЧЗ) — набор форматов для сканера камеры.
export const BARCODE_CAMERA_FORMATS = [BarcodeFormat.CODE_128, BarcodeFormat.EAN_13, BarcodeFormat.EAN_8];

export const BARCODE_FORMATS = [
  { id: "CODE128", label: "Code 128" },
  { id: "EAN13",   label: "EAN-13"   },
  { id: "EAN8",    label: "EAN-8"    },
];

// ─── КИЗ normalization (repair) ──────────────────────────────────────────────
// Приводит любой ввод (сканер / камера / вставка) к каноничному GS1-виду с
// реальными GS-разделителями. Используется для печати и для «Преобразованное».

export function normalizeCodeValue(raw) {
  let val = raw
    .replace(/^\]d[12]/i, "")
    .replace(/<GS>|\[GS\]|\{GS\}|\\u001d|\\x1d|\\x1D|\\035/g, GS);

  // USB scanner with Russian Windows keyboard layout: remap Cyrillic → Latin
  if (/[а-яёА-ЯЁ]/.test(val)) {
    val = val.split('').map(c => RU_TO_LATIN_MAP[c] ?? c).join('');
  }

  // HRI parentheses format from scanner: (01)GTIN(21)SERIAL(91)VK(92)CRYPTO
  // Convert to raw GS1 with GS separators between variable-length AIs.
  // Серийный номер (AI 21) и крипто-хвост (91/92/93) могут по спецификации GS1
  // содержать буквальную «(» как часть значения — нельзя резать по первой же
  // скобке ([^(]*). Сначала ищем позиции ВСЕХ настоящих маркеров «(NN)», затем
  // берём значение каждого AI до начала СЛЕДУЮЩЕГО найденного маркера.
  if (/^\s*\(\d/.test(val)) {
    const markers = [];
    const markerRx = /\((\d{2,4})\)/g;
    let mm;
    while ((mm = markerRx.exec(val)) !== null) {
      markers.push({ ai: mm[1], start: mm.index, end: mm.index + mm[0].length });
    }
    const parts = markers.map((mk, i) => {
      const valueEnd = i + 1 < markers.length ? markers[i + 1].start : val.length;
      return { ai: mk.ai, value: val.slice(mk.end, valueEnd).trim() };
    });
    if (parts.length > 0) {
      val = parts.reduce((acc, { ai, value }, i) => {
        const needsGs = i < parts.length - 1 && !GS1_FIXED_AIs.has(ai);
        return acc + ai + value + (needsGs ? GS : '');
      }, '');
    }
  }

  return val;
}

// ─── КИЗ diagnostics (analyze, no repair) ─────────────────────────────────────
// Разбирает СЫРОЙ ввод сканера как есть, чтобы диагностировать настройку сканера
// (иначе настоящий GS не отличить от текстового «<GS>»).

export function analyzeKiz(raw, { autoWrap = false } = {}) {
  const value = raw || "";
  const chars = [...value];
  const total = chars.length;
  const hidden = chars.filter((c) => c.charCodeAt(0) < 32).length; // GS и прочие управляющие
  const gsCount = value.split(GS).length - 1;
  const visible = total - hidden;
  const hasCyrillic = /[а-яёА-ЯЁ]/.test(value);
  const hasLiteralGs = /<GS>|\[GS\]|\{GS\}/i.test(value);

  // Разбор по реальным GS-разделителям
  const segs = value.split(GS);
  const first = segs[0] || "";
  const rest = segs.slice(1);

  const gtinMatch = first.match(/01(\d{14})/);
  const hasGtin = !!gtinMatch;
  let afterGtin = first;
  if (hasGtin) {
    const idx = first.indexOf(gtinMatch[0]);
    afterGtin = first.slice(idx + gtinMatch[0].length);
  }
  const hasSerial = /^21\S+/.test(afterGtin);
  const seg = (ai) => rest.some((s) => s.startsWith(ai));
  const has91 = seg("91");
  const has92 = seg("92");
  const has93 = seg("93");

  const structureOk = hasGtin && hasSerial && (has91 || has92);

  let verdict; // 'empty' | 'ok' | 'improve' | 'bad'
  if (!value) verdict = "empty";
  else if (!hasGtin || !structureOk) verdict = "bad";
  else if (hasCyrillic || !autoWrap) verdict = "improve";
  else verdict = "ok";

  return {
    total, visible, hidden, gsCount,
    hasCyrillic, hasLiteralGs,
    language: hasCyrillic ? "русский" : "английский",
    hasGtin, hasSerial, has91, has92, has93,
    structureOk, autoWrap,
    verdict,
  };
}

// ─── camera scanner hook ──────────────────────────────────────────────────────

export function useCameraScanner(onResult, formats = CAMERA_FORMATS) {
  const [open, setOpen]       = useState(false);
  const [error, setError]     = useState("");
  const [scanned, setScanned] = useState("");
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const videoRef              = useRef(null);
  const controlsRef           = useRef(null);

  const startScan = useCallback(() => {
    setError("");
    setScanned("");
    setOpen(true);
  }, []);

  const stopScan = useCallback(() => {
    if (controlsRef.current) {
      try { controlsRef.current.stop(); } catch {}
      controlsRef.current = null;
    }
    setOpen(false);
    setScanned("");
  }, []);

  const switchCamera = useCallback(() => {
    if (devices.length < 2) return;
    const currentIndex = Math.max(0, devices.findIndex((d) => d.deviceId === deviceId));
    const next = devices[(currentIndex + 1) % devices.length];
    if (!next) return;
    if (controlsRef.current) {
      try { controlsRef.current.stop(); } catch {}
      controlsRef.current = null;
    }
    setScanned("");
    setError("");
    setDeviceId(next.deviceId);
  }, [devices, deviceId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserMultiFormatReader(hints);

    const init = async () => {
      await new Promise((res) => setTimeout(res, 100));
      if (cancelled || !videoRef.current) return;
      try {
        const videoDevices = await BrowserMultiFormatReader.listVideoInputDevices();
        setDevices(videoDevices);
        const back = videoDevices.find((d) => /back|rear|environment|зад/i.test(d.label));
        const selectedDeviceId = deviceId || back?.deviceId || videoDevices[0]?.deviceId;
        if (selectedDeviceId && selectedDeviceId !== deviceId) setDeviceId(selectedDeviceId);
        const onDecode = (result) => {
          if (cancelled || !result) return;
          const text = result.getText();
          setScanned(text);
          navigator.vibrate?.(80);
          setTimeout(() => { if (!cancelled) { onResult(text); stopScan(); } }, 400);
        };
        const controls = deviceId
          ? await reader.decodeFromVideoDevice(selectedDeviceId, videoRef.current, onDecode)
          : await reader.decodeFromConstraints(
              { video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 1280 } } },
              videoRef.current,
              onDecode
            );
        if (!cancelled) controlsRef.current = controls;
      } catch (e) {
        if (!cancelled)
          setError(e?.message?.includes("Permission")
            ? "Нет доступа к камере. Разрешите доступ в браузере."
            : "Не удалось открыть камеру.");
      }
    };

    init();
    return () => {
      cancelled = true;
      if (controlsRef.current) { try { controlsRef.current.stop(); } catch {} controlsRef.current = null; }
    };
  }, [open, deviceId, onResult, stopScan, formats]);

  return { open, startScan, stopScan, switchCamera, videoRef, error, scanned, devices, deviceId };
}
